import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { CheckSquare, Search, Edit2, Trash2, Plus, SlidersHorizontal } from 'lucide-react';
import { useTasks } from '../context/TasksContext';
import { useLeads } from '../context/LeadsContext';
import { useCompany } from '../context/CompanyContext';
import { useRole } from '../context/RoleContext';
import { useAuth } from '../context/AuthContext';
import LeadInfoPopover from '../components/pipeline/LeadInfoPopover';
import { formatAddress, truncateText } from '../utils/helpers';
import { getDisplayName } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { useToast } from '../hooks/use-toast';
import NewTaskDialog from '../components/tasks/NewTaskDialog';
import TaskCardMobile from '../components/tasks/TaskCardMobile';


const TASK_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const Tasks = () => {
  // Logical separation (iteration 146): surface only `adminTasks` here.
  // Jobs are owned by /dispatch + /jobs; mixing them in this page caused
  // clutter and confused the "Tasks" mental model.
  const { adminTasks, updateTask, deleteTask } = useTasks();
  const tasks = adminTasks;
  const { leads } = useLeads();
  const { companyId } = useCompany();
  const { isTechnician } = useRole();
  const { user } = useAuth();
  const { toast } = useToast();

  const [companyUsers, setCompanyUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | completed
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all'); // all | me | unassigned
  const [showNewTask, setShowNewTask] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // mobile filter Sheet

  // View chip — Sprint 291 (Tasks V2): six preset views that filter the list
  // in the pattern operators actually think about their queue. Default to
  // 'my' for everyone; admins can flip to 'all' when they want oversight.
  //   my       → assignedTo === current user (default)
  //   today    → due date is today
  //   overdue  → due date is before today AND not completed
  //   upcoming → due date > today  OR  no due set
  //   completed→ status === completed
  //   all      → no view-specific filter
  const [view, setView] = useState('my');

  // Technicians default to viewing only their own tasks and cannot widen this scope
  useEffect(() => {
    if (isTechnician) setAssignedFilter('me');
  }, [isTechnician]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    taskType: 'call',
    priority: 'medium',
    dueDate: '',
    dueTime: '',
  });
  const [selectedLead, setSelectedLead] = useState(null);

  const itemsPerPage = 20;

  const leadMap = useMemo(() => {
    const map = {};
    leads.forEach((lead) => {
      map[lead.id] = {
        name: getDisplayName(lead),
        address: formatAddress(lead.address),
        lead,
      };
    });
    return map;
  }, [leads]);

  const assignedUserMap = useMemo(() => {
    const map = {};
    companyUsers.forEach((u) => {
      map[u.id] = u.displayName || u.name || u.email || u.id;
    });
    return map;
  }, [companyUsers]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!companyId) {
          setCompanyUsers([]);
          return;
        }
        const membershipsSnapshot = await getDocs(collection(db, 'companies', companyId, 'users'));
        const memberships = membershipsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const joined = await Promise.all(
          memberships.map(async (m) => {
            try {
              const profileSnap = await getDoc(doc(db, 'users', m.id));
              const profile = profileSnap.exists() ? profileSnap.data() : {};
              return { id: m.id, role: m.role, ...profile };
            } catch (e) {
              return { id: m.id, role: m.role };
            }
          })
        );
        setCompanyUsers(joined);
      } catch (err) {
        console.error('Failed to load company users for tasks page', err);
        setCompanyUsers([]);
      }
    };

    loadUsers();
  }, [companyId]);

  const parseDateTime = (task) => {
    if (task.type === 'job') {
      if (!task.scheduledStart) return null;
      const d = new Date(task.scheduledStart);
      return isNaN(d.getTime()) ? null : d;
    }

    if (!task.dueDate) return null;
    const iso = task.dueTime ? `${task.dueDate}T${task.dueTime}:00` : `${task.dueDate}T00:00:00`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const now = useMemo(() => new Date(), []);
  const startOfToday = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    [now]
  );
  const startOfTomorrow = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
    [now]
  );
  const startOfDayAfterTomorrow = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2),
    [now]
  );

  const filteredTasks = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return tasks.filter((task) => {
      const leadInfo = leadMap[task.leadId];
      const leadName = leadInfo?.name?.toLowerCase() || '';

      // ── View chip (Sprint 291) — applied first so it composes with the
      // legacy filter selects below ────────────────────────────────────────
      const dtForView = parseDateTime(task);
      switch (view) {
        case 'my':
          if (!user?.uid || task.assignedTo !== user.uid) return false;
          if (task.status === 'completed') return false;
          break;
        case 'today':
          if (!dtForView || dtForView < startOfToday || dtForView >= startOfTomorrow) return false;
          if (task.status === 'completed') return false;
          break;
        case 'overdue':
          if (!dtForView || dtForView >= startOfToday) return false;
          if (task.status === 'completed') return false;
          break;
        case 'upcoming':
          if (task.status === 'completed') return false;
          if (dtForView && dtForView < startOfTomorrow) return false;
          break;
        case 'completed':
          if (task.status !== 'completed') return false;
          break;
        case 'all':
        default:
          break;
      }

      // Status filter
      if (statusFilter === 'pending' && task.status !== 'pending') return false;
      if (statusFilter === 'completed' && task.status !== 'completed') return false;

      // Priority filter
      if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;

      // Type filter
      if (typeFilter !== 'all' && task.taskType !== typeFilter) return false;

      // Assigned filter
      if (assignedFilter === 'me') {
        if (!user?.uid || task.assignedTo !== user.uid) return false;
      } else if (assignedFilter === 'unassigned') {
        if (task.assignedTo) return false;
      }

      // Due filter
      const dt = parseDateTime(task);
      switch (dueFilter) {
        case 'today':
          if (!dt || dt < startOfToday || dt >= startOfTomorrow) return false;
          break;
        case 'tomorrow':
          if (!dt || dt < startOfTomorrow || dt >= startOfDayAfterTomorrow) return false;
          break;
        case 'overdue':
          if (!dt || dt >= startOfToday) return false;
          break;
        case 'no-due':
          if (dt) return false;
          break;
        default:
          break;
      }

      if (!search) return true;

      const title = (task.title || '').toLowerCase();
      const desc = (task.description || '').toLowerCase();

      return (
        title.includes(search) ||
        desc.includes(search) ||
        leadName.includes(search)
      );
    });
  }, [
    tasks,
    searchTerm,
    statusFilter,
    priorityFilter,
    typeFilter,
    dueFilter,
    assignedFilter,
    view,
    leadMap,
    startOfToday,
    startOfTomorrow,
    startOfDayAfterTomorrow,
    user,
  ]);

  const sortedTasks = useMemo(() => {
    const pending = [];
    const completed = [];

    filteredTasks.forEach((task) => {
      if (task.status === 'completed') completed.push(task);
      else pending.push(task);
    });

    const compareByDue = (a, b) => {
      const aDate = parseDateTime(a);
      const bDate = parseDateTime(b);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    };

    const pendingOverdue = pending.filter((t) => {
      const dt = parseDateTime(t);
      return dt && dt < startOfToday;
    });
    const pendingUpcoming = pending.filter((t) => {
      const dt = parseDateTime(t);
      return !dt || dt >= startOfToday;
    });

    pendingOverdue.sort(compareByDue);
    pendingUpcoming.sort(compareByDue);
    completed.sort(compareByDue);

    return [...pendingOverdue, ...pendingUpcoming, ...completed];
  }, [filteredTasks, startOfToday]);

  const totalPages = Math.ceil(sortedTasks.length / itemsPerPage) || 1;

  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedTasks.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedTasks, currentPage]);

  const beginEdit = (task) => {
    setEditingTaskId(task.id);
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      taskType: task.taskType || 'call',
      priority: task.priority || 'medium',
      dueDate: task.dueDate || '',
      dueTime: task.dueTime || '',
    });
  };

  const resetEdit = () => {
    setEditingTaskId(null);
    setEditForm({
      title: '',
      description: '',
      taskType: 'call',
      priority: 'medium',
      dueDate: '',
      dueTime: '',
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingTaskId || !editForm.title.trim()) return;

    await updateTask(editingTaskId, {
      title: editForm.title.trim(),
      description: editForm.description.trim() || '',
      taskType: editForm.taskType,
      priority: editForm.priority,
      dueDate: editForm.dueDate || null,
      dueTime: editForm.dueTime || null,
    });
    resetEdit();
  };

  const handleToggleStatus = async (task) => {
    // Frontend permission hint for technicians: they can only update tasks assigned to them.
    if (isTechnician && user?.uid && task.assignedTo && task.assignedTo !== user.uid) {
      toast({
        title: 'Permission denied',
        description: "You don’t have permission to update this task.",
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateTask(task.id, {
        status: task.status === 'pending' ? 'completed' : 'pending',
      });
    } catch (error) {
      console.error('Failed to update task status', error);
      toast({
        title: 'Update failed',
        description: "You don’t have permission to update this task.",
        variant: 'destructive',
      });
    }
  };

  const renderDueCell = (task) => {
    const dt = parseDateTime(task);
    const isCompleted = task.status === 'completed';

    if (!dt) {
      return <span className="text-xs text-gray-400">No schedule</span>;
    }

    const isOverdue = dt < startOfToday;
    const isToday = dt >= startOfToday && dt < startOfTomorrow;

    const baseClass = isOverdue && !isCompleted
      ? 'text-xs text-red-600'
      : 'text-xs text-gray-700';

    const label = (() => {
      const isJob = task.type === 'job';
      if (isJob && task.scheduledStart) {
        const iso = task.scheduledStart;
        const date = iso.slice(0, 10);
        const time = iso.slice(11, 16);
        return `${date}${time ? ` · ${time}` : ''}`;
      }
      if (!isJob && task.dueDate) {
        return `${task.dueDate}${task.dueTime ? ` · ${task.dueTime}` : ''}`;
      }
      return 'No schedule';
    })();

    return (
      <div className="flex items-center gap-1">
        <span className={baseClass}>{label}</span>
        {isToday && !isCompleted && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-[rgba(242,107,29,0.1)] text-[var(--cb-orange)]">
            Today
          </span>
        )}
      </div>
    );
  };

  const renderPriorityBadge = (priority) => {
    if (!priority) return null;
    return (
      <Badge
        variant="outline"
        className={`text-[10px] px-1 py-0 ${
          priority === 'high'
            ? 'border-red-500 text-red-600'
            : priority === 'low'
            ? 'border-gray-400 text-gray-500'
            : 'border-yellow-500 text-yellow-600'
        }`}
      >
        {priority}
      </Badge>
    );
  };

  const renderTypeBadge = (taskType) => {
    if (!taskType) return null;
    return (
      <Badge variant="outline" className="text-[10px] px-1 py-0">
        {taskType}
      </Badge>
    );
  };

  const renderCategoryBadge = (task) => {
    const label = task.type === 'job' ? 'Job' : 'Internal';
    return (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 rounded-full border-gray-300 text-gray-600 bg-gray-50"
      >
        {label}
      </Badge>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <CheckSquare className="h-8 w-8 text-primary shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-secondary">Tasks</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Callbacks, follow-ups, and internal workflow items.
            </p>
          </div>
        </div>
        {!isTechnician && (
          <Button
            onClick={() => setShowNewTask(true)}
            className="text-white shrink-0"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="new-task-open-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">New Task</span>
          </Button>
        )}
      </div>

      {/* View chip strip — Sprint 291 (Tasks V2). Horizontally scrollable on
          mobile so all 6 chips remain reachable at 390px. Default view is
          'my'; admins can flip to 'all' for team oversight. */}
      <div
        className="flex overflow-x-auto -mx-1 px-1 py-1 gap-2 no-scrollbar"
        data-testid="tasks-view-strip"
      >
        {[
          { id: 'my',        label: 'My Tasks' },
          { id: 'today',     label: 'Today' },
          { id: 'overdue',   label: 'Overdue' },
          { id: 'upcoming',  label: 'Upcoming' },
          { id: 'completed', label: 'Completed' },
          { id: 'all',       label: 'All' },
        ].map((chip) => {
          const active = view === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => { setView(chip.id); setCurrentPage(1); }}
              data-testid={`tasks-view-chip-${chip.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-[var(--cb-orange)] text-white border-transparent'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {/* Edit bar */}
      {editingTaskId && (
        <form
          onSubmit={handleEditSubmit}
          className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: 'var(--cb-navy)' }}>
              Edit Task
            </span>
            <button
              type="button"
              onClick={resetEdit}
              className="text-xs text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="Task title"
              value={editForm.title}
              onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
              className="h-8 text-sm"
            />
            <Input
              placeholder="Description"
              value={editForm.description}
              onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              className="h-8 text-sm"
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Input
              type="date"
              value={editForm.dueDate}
              onChange={(e) => setEditForm((prev) => ({ ...prev, dueDate: e.target.value }))}
              className="h-8 text-xs"
            />
            <Input
              type="time"
              value={editForm.dueTime}
              onChange={(e) => setEditForm((prev) => ({ ...prev, dueTime: e.target.value }))}
              className="h-8 text-xs"
            />
            <Select
              value={editForm.taskType}
              onValueChange={(value) => setEditForm((prev) => ({ ...prev, taskType: value }))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TASK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={editForm.priority}
              onValueChange={(value) => setEditForm((prev) => ({ ...prev, priority: value }))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-xs">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              Save Changes
            </Button>
          </div>
        </form>
      )}

      {/* Filters Row */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Search — visible at every breakpoint */}
          <div className="relative md:w-1/3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by task or lead..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-sm"
            />
          </div>

          {/* Mobile: single Filters button opens a Sheet with all filters.
              Prevents forcing a 6-column grid of desktop selects into 390px. */}
          <div className="md:hidden">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(true)}
              data-testid="tasks-filters-open-mobile"
              className="w-full"
            >
              <SlidersHorizontal className="w-4 h-4 mr-1" />
              Filters
            </Button>
          </div>

          {/* Desktop (md+) filter grid — hidden on mobile */}
          <div className="hidden md:grid md:grid-cols-6 gap-2 md:gap-3 text-xs w-full md:w-auto">
            {/* Status */}
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Status</SelectItem>
                <SelectItem value="pending" className="text-xs">Pending</SelectItem>
                <SelectItem value="completed" className="text-xs">Completed</SelectItem>
              </SelectContent>
            </Select>

            {/* Priority */}
            <Select
              value={priorityFilter}
              onValueChange={(v) => {
                setPriorityFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Priority</SelectItem>
                <SelectItem value="high" className="text-xs">High</SelectItem>
                <SelectItem value="medium" className="text-xs">Medium</SelectItem>
                <SelectItem value="low" className="text-xs">Low</SelectItem>
              </SelectContent>
            </Select>

            {/* Type */}
            <Select
              value={typeFilter}
              onValueChange={(v) => {
                setTypeFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Types</SelectItem>
                {TASK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Assigned To — technicians are locked to "me" so we only show that option */}
            <Select
              value={assignedFilter}
              onValueChange={(v) => {
                if (!isTechnician) {
                  setAssignedFilter(v);
                  setCurrentPage(1);
                }
              }}
              disabled={isTechnician}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Assigned" />
              </SelectTrigger>
              <SelectContent>
                {!isTechnician && (
                  <SelectItem value="all" className="text-xs">All Assignees</SelectItem>
                )}
                <SelectItem value="me" className="text-xs">Assigned to me</SelectItem>
                {!isTechnician && (
                  <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                )}
              </SelectContent>
            </Select>

            {/* Due */}
            <Select
              value={dueFilter}
              onValueChange={(v) => {
                setDueFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Due" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Due</SelectItem>
                <SelectItem value="today" className="text-xs">Today</SelectItem>
                <SelectItem value="tomorrow" className="text-xs">Tomorrow</SelectItem>
                <SelectItem value="overdue" className="text-xs">Overdue</SelectItem>
                <SelectItem value="no-due" className="text-xs">No Due Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Dispatch hint — field jobs live elsewhere.
          Always-visible helper so operators don't look for jobs in this page. */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800"
        data-testid="tasks-dispatch-hint"
      >
        <span>Field jobs are managed under</span>
        <Link
          to="/dispatch"
          className="font-medium text-[var(--cb-orange)] hover:underline"
          data-testid="tasks-dispatch-link"
        >
          Dispatch →
        </Link>
      </div>

      {/* Task List — Desktop table (lg+) */}
      <div className="hidden lg:block bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Done</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Task</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Lead</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Schedule</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Priority</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Category</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Assigned To</th>
                {!isTechnician && (
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedTasks.map((task) => {
                const leadInfo = leadMap[task.leadId];
                const isCompleted = task.status === 'completed';

                return (
                  <tr key={task.id} className={`relative ${isCompleted ? 'opacity-60' : ''}`}>
                    {/* Done */}
                    <td className="px-4 py-2 align-top">
                      <Checkbox
                        checked={isCompleted}
                        onCheckedChange={() => handleToggleStatus(task)}
                      />
                    </td>

                    {/* Task */}
                    <td className="px-4 py-2 align-top">
                      <div className="flex flex-col">
                        <span
                          className={`font-medium text-sm ${
                            isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
                          }`}
                        >
                          {(task.title || '').trim() || <span className="italic text-gray-400">[Untitled]</span>}
                        </span>
                        {task.description && (
                          <span className="text-xs text-gray-500">
                            {truncateText(task.description, 80)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Lead */}
                    <td className="px-4 py-2 align-top">
                      {leadInfo ? (
                        <button
                          type="button"
                          className="text-xs text-[var(--cb-orange)] hover:underline"
                          onClick={() => setSelectedLead(leadInfo.lead)}
                        >
                          {leadInfo.name}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">Unknown lead</span>
                      )}

                      {selectedLead && selectedLead.id === task.leadId && (
                        <div className="absolute right-4 top-2 z-20">
                          <LeadInfoPopover
                            lead={selectedLead}
                            onClose={() => setSelectedLead(null)}
                            position="left"
                          />
                        </div>
                      )}
                    </td>

                    {/* Schedule */}
                    <td className="px-4 py-2 align-top">
                      {renderDueCell(task)}
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-2 align-top">
                      {renderPriorityBadge(task.priority)}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-2 align-top">
                      {renderTypeBadge(task.taskType)}
                    </td>

                    {/* Category */}
                    <td className="px-4 py-2 align-top">
                      {renderCategoryBadge(task)}
                    </td>

                    {/* Assigned To */}
                    <td className="px-4 py-2 align-top">
                      <span className="text-xs text-gray-700">
                        {task.assignedTo ? (assignedUserMap[task.assignedTo] || 'Assigned') : 'Unassigned'}
                      </span>
                    </td>

                    {/* Actions — admins/dispatchers only; technicians cannot edit or delete tasks */}
                    {!isTechnician && (
                      <td className="px-4 py-2 align-top text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => beginEdit(task)}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Edit task"
                            data-testid={`task-edit-btn-${task.id}`}
                          >
                            <Edit2 className="h-4 w-4 text-gray-500" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteTask(task.id)}
                            className="p-1 rounded hover:bg-red-50"
                            title="Delete task"
                            data-testid={`task-delete-btn-${task.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

              {paginatedTasks.length === 0 && (
                <tr>
                  <td colSpan={isTechnician ? 8 : 9} className="px-4 py-6 text-center text-sm text-gray-400">
                    No tasks found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task List — Mobile cards (<lg). Same paginated data, different
          presentation. Tap a card body to edit. Checkbox toggles completion. */}
      <div className="lg:hidden space-y-2" data-testid="tasks-mobile-list">
        {paginatedTasks.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-sm text-gray-400">
            No tasks found for the selected filters.
          </div>
        ) : (
          paginatedTasks.map((task) => (
            <TaskCardMobile
              key={task.id}
              task={task}
              clientName={leadMap[task.leadId]?.name || null}
              assigneeName={task.assignedTo ? (assignedUserMap[task.assignedTo] || 'Assigned') : null}
              onEdit={beginEdit}
              onToggleStatus={handleToggleStatus}
              now={now}
              startOfToday={startOfToday}
              isTechnician={isTechnician}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {sortedTasks.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, sortedTasks.length)} to
            {' '}
            {Math.min(currentPage * itemsPerPage, sortedTasks.length)} of {sortedTasks.length}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Sprint 291 — Tasks V2 creation modal. Routes writes through
          `addAdminTask` so `type: 'task'` is enforced by the typed writer. */}
      <NewTaskDialog
        open={showNewTask}
        onOpenChange={setShowNewTask}
        defaultAssigneeUid={user?.uid}
      />

      {/* Mobile filter Sheet — same filter primitives as desktop, but drawer'd
          instead of inline so a 390px viewport isn't force-fit with a 6-column
          grid. Filters here mutate the same state as the desktop selects. */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl" data-testid="tasks-filters-sheet">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 pt-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Priority</label>
              <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setCurrentPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Type</label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {TASK_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isTechnician && (
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assignee</label>
                <Select value={assignedFilter} onValueChange={(v) => { setAssignedFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    <SelectItem value="me">Assigned to me</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Due</label>
              <Select value={dueFilter} onValueChange={(v) => { setDueFilter(v); setCurrentPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Due</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="tomorrow">Tomorrow</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="no-due">No Due Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStatusFilter('all');
                setPriorityFilter('all');
                setTypeFilter('all');
                setAssignedFilter(isTechnician ? 'me' : 'all');
                setDueFilter('all');
                setCurrentPage(1);
                setShowFilters(false);
              }}
              className="w-full"
              data-testid="tasks-filters-reset"
            >
              Reset
            </Button>
            <Button
              type="button"
              onClick={() => setShowFilters(false)}
              className="w-full text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="tasks-filters-apply"
            >
              Apply
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Tasks;
